import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MessageCircle, Send, Copy, ExternalLink, Zap, 
  Users, FileText, ChevronRight, Sparkles, Plus
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function WhatsAppToolsPage() {
  const [templates, setTemplates] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [whatsappLink, setWhatsappLink] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Bulk message state
  const [bulkContacts, setBulkContacts] = useState("");
  const [bulkMessages, setBulkMessages] = useState([]);

  useEffect(() => {
    document.title = "WhatsApp Marketing Tools | MarkeeMix";
    fetchTemplates();
    fetchQuickReplies();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/templates`);
      const data = await res.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (e) {
      console.error("Failed to fetch templates", e);
    }
  };

  const fetchQuickReplies = async () => {
    try {
      const res = await fetch(`${API}/api/whatsapp/quick-replies`);
      const data = await res.json();
      if (data.success) {
        setQuickReplies(data.quick_replies);
      }
    } catch (e) {
      console.error("Failed to fetch quick replies", e);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    // Initialize variables
    const vars = {};
    template.variables.forEach(v => vars[v] = "");
    setVariables(vars);
    setGeneratedMessage("");
    setWhatsappLink("");
  };

  const generateMessage = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/whatsapp/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          variables: variables
        })
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedMessage(data.message);
        setWhatsappLink(data.whatsapp_link);
        toast.success("Message generated!");
      }
    } catch (e) {
      toast.error("Failed to generate message");
    } finally {
      setLoading(false);
    }
  };

  const generateBulkMessages = async () => {
    if (!selectedTemplate || !bulkContacts.trim()) return;
    
    // Parse contacts (format: name,phone per line)
    const contacts = bulkContacts.trim().split("\n").map(line => {
      const [name, phone, ...rest] = line.split(",").map(s => s.trim());
      return { name, phone, ...variables };
    }).filter(c => c.name && c.phone);
    
    if (contacts.length === 0) {
      toast.error("No valid contacts found");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/whatsapp/bulk-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          contacts: contacts
        })
      });
      const data = await res.json();
      if (data.success) {
        setBulkMessages(data.messages);
        toast.success(`Generated ${data.count} messages!`);
      }
    } catch (e) {
      toast.error("Failed to generate messages");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white">
            <MessageCircle className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            WhatsApp Marketing Tools
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Create professional WhatsApp messages with templates. Perfect for customer support, marketing, and notifications.
          </p>
        </div>

        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Messages</TabsTrigger>
            <TabsTrigger value="quick">Quick Replies</TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Template List */}
              <Card className="p-4">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  Message Templates
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => selectTemplate(t)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedTemplate?.id === t.id
                          ? "bg-green-100 border-2 border-green-500"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{t.name}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-1">{t.template.slice(0, 60)}...</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Message Generator */}
              <Card className="p-4">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  Generate Message
                </h3>
                
                {selectedTemplate ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="text-gray-600 whitespace-pre-wrap">{selectedTemplate.template}</p>
                    </div>
                    
                    <div className="space-y-3">
                      {selectedTemplate.variables.map((v) => (
                        <div key={v}>
                          <label className="text-sm font-medium text-gray-700 capitalize">
                            {v.replace(/_/g, " ")}
                          </label>
                          <Input
                            placeholder={`Enter ${v.replace(/_/g, " ")}`}
                            value={variables[v] || ""}
                            onChange={(e) => setVariables({...variables, [v]: e.target.value})}
                          />
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      onClick={generateMessage} 
                      disabled={loading}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Generate Message
                    </Button>
                    
                    {generatedMessage && (
                      <div className="space-y-3">
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <p className="whitespace-pre-wrap">{generatedMessage}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => copyToClipboard(generatedMessage)}
                            className="flex-1"
                          >
                            <Copy className="w-4 h-4 mr-2" /> Copy
                          </Button>
                          <Button 
                            asChild
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <a href={whatsappLink} target="_blank" rel="noreferrer">
                              <Send className="w-4 h-4 mr-2" /> Open WhatsApp
                            </a>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a template to get started</p>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Bulk Messages Tab */}
          <TabsContent value="bulk">
            <Card className="p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Bulk Message Generator
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Select Template</label>
                    <select
                      className="w-full mt-1 p-2 border rounded-md"
                      value={selectedTemplate?.id || ""}
                      onChange={(e) => {
                        const t = templates.find(t => t.id === e.target.value);
                        if (t) selectTemplate(t);
                      }}
                    >
                      <option value="">Choose template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">
                      Contacts (name, phone - one per line)
                    </label>
                    <Textarea
                      placeholder="John Doe, +919876543210\nJane Smith, +919876543211"
                      value={bulkContacts}
                      onChange={(e) => setBulkContacts(e.target.value)}
                      className="min-h-[150px] font-mono text-sm"
                    />
                  </div>
                  
                  {selectedTemplate && selectedTemplate.variables.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Common Variables</label>
                      {selectedTemplate.variables.filter(v => v !== "customer_name").map(v => (
                        <Input
                          key={v}
                          placeholder={v.replace(/_/g, " ")}
                          value={variables[v] || ""}
                          onChange={(e) => setVariables({...variables, [v]: e.target.value})}
                        />
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    onClick={generateBulkMessages}
                    disabled={loading || !selectedTemplate}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    Generate {bulkContacts.split("\n").filter(l => l.trim()).length} Messages
                  </Button>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Generated Messages</label>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {bulkMessages.length > 0 ? bulkMessages.map((msg, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium">{msg.name}</span>
                          <a 
                            href={msg.whatsapp_link} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-green-600 hover:underline flex items-center gap-1"
                          >
                            Send <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <p className="text-gray-600 whitespace-pre-wrap text-xs">{msg.message.slice(0, 100)}...</p>
                      </div>
                    )) : (
                      <div className="text-center text-gray-400 py-12">
                        Generated messages will appear here
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Quick Replies Tab */}
          <TabsContent value="quick">
            <Card className="p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                Quick Replies
              </h3>
              <p className="text-gray-600 mb-4">
                One-click copy for common customer service responses
              </p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {quickReplies.map((qr, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="justify-start h-auto py-3 px-4 text-left"
                    onClick={() => copyToClipboard(qr.text)}
                  >
                    <span className="mr-2 text-lg">{qr.emoji}</span>
                    <span className="text-sm">{qr.text}</span>
                  </Button>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <Card className="p-8 mt-8 text-center bg-gradient-to-r from-green-600 to-green-700 text-white">
          <h2 className="text-2xl font-bold mb-2">Need More Marketing Tools?</h2>
          <p className="opacity-90 mb-4">Explore 75+ tools for SEO, content, and social media</p>
          <Button asChild size="lg" className="bg-white text-green-700 hover:bg-gray-100">
            <a href="/tools">View All Tools</a>
          </Button>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
